import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTimes } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import eventEmitter from "../../../../components/eventEmitter";
import Btn from "../../../../components/atoms/Btn";

function PositionEditor({ position, deletePos, edit, select }) {
  const deleting = "deleting";
  const editing = "editing";

  const rowRef = useRef(null);
  const { pos, desc } = position;
  const [description, setDescription] = useState(desc);
  const [action, setAction] = useState(null);

  function submit() {
    edit({ ...position, desc: description });
    setAction(null);
  }

  function deleteItem() {
    setTimeout(() => {
      deletePos(position);
    }, 0);
  }

  useEffect(() => {
    if (action) {
      const sub = eventEmitter.clickTracker$.subscribe(({ target }) => {
        const { current } = rowRef;
        if (current && !current.contains(target)) {
          setAction(null);
        }
      });
      return () => sub.unsubscribe();
    }
    return () => {};
  }, [action]);

  return (
    <div
      className={`tw-border-b border-1 ${action ? "bg-2" : "bg-1"}`}
      ref={rowRef}
    >
      <div className="tw-w-full tw-flex tw-relative">
        <div className="tw-w-4" />

        <div className="tw-w-48 tw-text-left tw-my-2">
          <Btn
            className="tw-bg-transparent hover:tw-underline"
            onSubmit={() => select(position)}
          >
            <div
              title={`open: ${pos}`}
              className="tw-truncate tw-w-36 tw-text-left"
            >
              {pos}
            </div>
          </Btn>
        </div>
        <div className="tw-w-2" />
        <div
          onKeyDown={(e) => e.code === "Enter" && setAction(editing)}
          tabIndex={-1}
          onClick={() => setAction(editing)}
          className="tw-w-32 tw-break-all tw-my-2 tw-font-light tw-italic"
        >
          {desc || "no label"}
        </div>
        <div className="tw-w-6" />
        <div className="show-parent-hover tw-my-2">
          <Btn
            title="edit item"
            onSubmit={() => setAction(editing)}
            className="tw-w-4 tw-mx-auto tw-bg-transparent"
          >
            <FontAwesomeIcon icon={faEdit} />
          </Btn>
        </div>
        <div className="tw-w-2" />
        <div className="show-parent-hover tw-my-2">
          <Btn
            title="delete item"
            onSubmit={() => setAction(deleting)}
            className="tw-w-4 tw-mx-auto tw-bg-transparent"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Btn>
        </div>
        <div className="tw-w-4" />
      </div>
      <div className={action === editing ? "tw-block" : "tw-hidden"}>
        <div className="tw-p-4">
          <div className="tw-opacity-80">Type new label for {pos}:</div>

          <div className="tw-flex tw-items-center">
            <div className="tw-py-2">
              {action === editing ? (
                <input
                  value={description}
                  autoFocus={true}
                  onKeyDown={(e) => e.code === "Enter" && submit()}
                  onChange={(e) => setDescription(e.target.value)}
                  className="tw-w-32 bg-1 tw-border border-2 border-3 tw-h-6"
                />
              ) : null}
            </div>
            <div className="tw-w-4" />
            <Btn
              onSubmit={() => setAction(false)}
              className="tw-px-1 tw-text-2xs tw-inline-block prevent-lose-focus tw-text-primary"
            >
              Cancel
            </Btn>
            <div className="tw-w-4" />
            <Btn
              onSubmit={() => submit()}
              className="tw-px-1 tw-py-0.5 tw-bg-primary tw-text-white tw-uppercase tw-text-2xs tw-inline-block prevent-lose-focus"
            >
              Save
            </Btn>
            <div className="tw-w-6" />
          </div>
        </div>
      </div>

      <div className={action === deleting ? "tw-block" : "tw-hidden"}>
        <div className="tw-flex tw-items-center tw-h-10 tw-p-4">
          <div className="tw-flex-grow" />
          <Btn
            onSubmit={() => setAction(null)}
            className="tw-capitalize tw-border tw-py-0.5 tw-px-1 tw-border-red-500 tw-text-red-500 prevent-lose-focus"
          >
            cancel
          </Btn>
          <div className="tw-w-2" />
          <Btn
            onSubmit={() => deleteItem(position)}
            className="tw-capitalize tw-py-0.5 tw-px-1 tw-bg-red-500 tw-text-white prevent-lose-focus"
          >
            confirm delete
          </Btn>
        </div>
      </div>
    </div>
  );
}

PositionEditor.propTypes = {
  position: PropTypes.shape({
    pos: PropTypes.string,
    desc: PropTypes.string,
  }).isRequired,
  select: PropTypes.func.isRequired,
  deletePos: PropTypes.func.isRequired,
  edit: PropTypes.func.isRequired,
};

export default React.memo(PositionEditor);
